import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, Typography, Container } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import OtpInput from 'react-otp-input';
import useAppStore from '../../store/appStore';
import api from '../../api/apiClient';
import { handleError } from '../../utils/error-handling';
import { handlePopup } from '../../utils/popup-service';

const OTPPage = () => {
  const [otp, setOtp] = useState('');
  const [isPageReady, setIsPageReady] = useState(false);
  const navigate = useNavigate();
  const state = useLocation().state;
  const maskedNumber = state?.maskedNumber;
  const { t } = useTranslation();
  const { setAccessLevel, language } = useAppStore();

  useEffect(() => {
    if (otp.length === 6) {
      // Create a synthetic event object
      const syntheticEvent = {
        preventDefault: () => {},
      };
      handleSubmit(syntheticEvent);
    }
  }, [otp]);

  useEffect(() => {
    // Only show the popup when the page is ready
    if (isPageReady && maskedNumber) {
      handlePopup('info', t('otpPage.info'));
    }
  }, [isPageReady]);

  // Set page as ready after initial render and verification
  useEffect(() => {
    if (maskedNumber) {
      setIsPageReady(true);
    } else {
      // Redirect if no masked number is present
      navigate('/');
    }

    return () => {
      setIsPageReady(false);
    };
  }, [maskedNumber]);

  const handleResendOTP = async () => {
    try {
      await api.post('/auth/otp/resend');
    } catch (error) {
      handleError(
        'error',
        error.response?.data?.error[language] || t('otpPage.invalidOtp')
      );
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (otp.length !== 6 || !/^\d+$/.test(otp)) {
      handleError('error', t('otpPage.invalidOtp'));
      return;
    }
    try {
      const res = await api.post('/auth/otp', { otp });
      if (res.status === 200) {
        setAccessLevel(2);
        navigate('/face');
      } else {
        setOtp('');
        handleError(
          'error',
          res.data.error.tr
            ? t(`otpPage.${res.data.error.tr}`)
            : t('otpPage.invalidOtp')
        );
      }
    } catch (error) {
      setOtp('');
      handleError(
        'error',
        error.response?.data?.tr
          ? t(`otpPage.${error.response?.data?.tr}`)
          : t('otpPage.invalidOtp')
      );
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography variant="h4" fontSize={20}>
          {t('otpPage.title')}
        </Typography>
        <Box>
          <Typography
            variant="body1"
            color="danger"
            fontSize={30}
            sx={{
              fontWeight: 'bold',
            }}
            style={{
              direction: 'ltr',
            }}
          >
            {maskedNumber}
          </Typography>
        </Box>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3, px: 2 }}>
          <OtpInput
            value={otp}
            onChange={setOtp}
            numInputs={6}
            shouldAutoFocus
            inputType="number"
            renderSeparator={<span style={{ margin: '0 4px' }}>○</span>}
            renderInput={props => <input {...props} />}
            inputStyle={{
              width: '2.5rem',
              height: '2.5rem',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '1rem',
              textAlign: 'center',
              margin: '0 2px',
              '@media (max-width: 400px)': {
                width: '2rem',
                height: '2rem',
                fontSize: '0.875rem',
              },
            }}
            containerStyle={{
              direction: 'ltr',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '95%',
              maxWidth: '350px',
              margin: '0 auto',
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="soft"
            color="success"
            sx={{ mt: 3, mb: 2 }}
          >
            {t('otpPage.verify')}
          </Button>

          <Button
            variant="plain"
            color="primary"
            fullWidth
            sx={{ mt: 3, mb: 2 }}
            onClick={handleResendOTP}
          >
            {t('otpPage.resendOTP')}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default OTPPage;
